import {Build, Component, Element, h, Host, Prop, State, Watch} from "@stencil/core"
import {IconClass, IconColors, type IconColorType, IconSizesType} from "./constants"
import {fetchIcon} from "../../utils/icon.utils"
import {govHost} from "../../helpers/Dom/template"
import {GovHost} from "../../helpers/Dom/template.types"
import {iconsLazyLoad} from "../../helpers/Dom/win"
import {validateProp} from "../../helpers/Validate/props";
import {Sizes} from "../../core/constants/sizes";
import {isValidSVG} from "../../helpers/Dom/document";

@Component({
	assetsDirs: ["assets"],
	tag: "gov-icon",
})
export class GovIcon {
	private h: GovHost
	private intersectionObserver: IntersectionObserver

	constructor() {
		this.h = govHost(this.host)
	}

	@Element() host: HTMLGovIconElement
	/**
	 * Icon size
	 */
	@Prop({reflect: true}) readonly size?: IconSizesType
	/**
	 * Icon color
	 */
	@Prop({reflect: true}) readonly color?: IconColorType
	/**
	 * Name of icon in folder
	 */
	@Prop({reflect: true}) name: string = null
	/**
	 * Type (folder) of icon
	 */
	@Prop({reflect: true}) type = "components"

	@State() private visible = false
	@State() private error = false

	@Watch("size")
	validateSize(newValue: string): void {
		validateProp(Sizes, newValue, IconClass.root)
	}

	@Watch("color")
	validateColor(newValue: string): void {
		validateProp(IconColors, newValue, IconClass.root)
	}

	@Watch("name")
	private async loadIconPathData(): Promise<void> {
		const {name, visible} = this
		if (!Build.isBrowser || !name || !visible) {
			return
		}
		await this.loadIcon()
	}

	connectedCallback(): void {
		if (iconsLazyLoad()) {
			this.visible = true
			this.loadIconPathData().finally()
		} else {
			this.waitUntilVisible(async () => {
				this.visible = true
				await this.loadIconPathData()
			})
		}
	}

	disconnectedCallback(): void {
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
			this.intersectionObserver = null
		}
	}

	async componentWillLoad(): Promise<void> {
		this.validateSize(this.size)
		this.validateColor(this.color)
		await this.loadIconPathData()
	}

	private waitUntilVisible(callback: () => void): void {
		if (!Build.isBrowser || typeof window === "undefined" || !(window as any).IntersectionObserver) {
			callback()
			return
		}
		this.intersectionObserver = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						if (this.intersectionObserver) {
							this.intersectionObserver.disconnect()
							this.intersectionObserver = null
						}
						callback()
					}
				})
			},
			{rootMargin: "50px"},
		)
		this.intersectionObserver.observe(this.host)
	}

	private async loadIcon() {
		try {
			const {name, type} = this
			const data = await fetchIcon({name, type})
			const isValid = isValidSVG(data)
			this.host.innerHTML = isValid ? data : null;
		} catch (e) {
			this.host.innerHTML = ''
			this.error = true
		}
	}

	render() {
		if (this.error) {
			return null
		}
		return (
			<Host class={this.h.classes(IconClass.root)} size={this.size} color={this.color}/>
		)
	}
}
